<script setup lang="ts">
import { InputSwitch } from '@caks/uikit/input'
import { getMaxFilledPickup, getPickupPoints, getUserAddresses } from '@caks/shared/api/user.route'
import { IconPlus } from '@caks/uikit/icon'
import { InputRadio } from '@caks/uikit/input'
import { useAccountStore } from '@caks/shared/store/store.account'
import { ButtonMain } from '@caks/uikit/button'
import { Address, PickupPoint } from '@caks/shared/types/types.api'
import { OptionInList } from '~/types/options'

interface Emit {
  (emit: 'close', id: string): void
  (emit: 'change-visible', value: 'new-address'): void
}

const accountStore = useAccountStore()
const emit = defineEmits<Emit>()

const mockPickup = shallowRef<OptionInList[]>([])
const mockUserAddress = shallowRef<OptionInList[]>([])

const selectedAddress = ref(accountStore.selectedLocation?.title)
const selectedDeliveryAddress = ref(accountStore.selectedLocation?.id)

const deliveryIsActive = ref(false)
const maxFillerLocationId = ref(0)
const maxFilledLocationIiko = ref<string>('')

const titles = computed(() => {
  return deliveryIsActive.value
    ? ['Выбрать адрес самовывоза', 'Выбрать адрес доставки']
    : ['Выбрать адрес самовывоза']
})

const orderTypes = ['pickup', 'delivery']

const pageTitle = computed(() => {
  return titles.value[chosenOption.value]
})

const chosenOption = ref(0)

const coordinates = ref<{ lon: number; lat: number }[]>([])

const isButtonDisabled = computed(() => {
  if (chosenOption.value === 1) {
    return !selectedDeliveryAddress.value
  } else {
    return !selectedAddress.value
  }
})

const setLocation = () => {
  if (chosenOption.value === 1) {
    const address = mockUserAddress.value.find(
      (el) => el.id === selectedDeliveryAddress.value
    ) as OptionInList
    accountStore.setLocation(
      {
        id: address.id as number,
        title: address.title,
        iiko_id: address.iiko_id,
      },
      'delivery'
    )
    localStorage.setItem('order', 'delivery')
    localStorage.setItem('location', JSON.stringify(address))

    emit('close', maxFilledLocationIiko.value)
  } else {
    const pickupInfo = mockPickup.value.find(
      (el) => el.title === selectedAddress.value
    ) as OptionInList

    const address = {
      id: pickupInfo.id,
      title: pickupInfo.title,
      iiko_id: pickupInfo.iiko_id,
    }

    accountStore.setLocation(
      {
        id: address.id as number,
        title: address.title,
        iiko_id: address.iiko_id,
      },
      'pickup'
    )
    localStorage.setItem('order', 'pickup')
    localStorage.setItem('location', JSON.stringify(address))

    emit('close', address.iiko_id!)
  }
}

const generatePickupList = (list: PickupPoint[]): OptionInList[] => {
  const newArray: OptionInList[] = []

  list.forEach((point) => {
    if (point.id === +accountStore.cityId)
      coordinates.value.push({ lon: point.lon, lat: point.lat })
    //@ts-ignore
    newArray.push({
      id: point.id,
      iiko_id: point.iiko_id,
      path: `/delivery/point/${point.id}/info`,
      title: point.name,
      subtitle: `С ${point.open_from} до ${point.open_to}`,
    })
  })

  return newArray
}

const generateAddressesList = (list: Address[]): OptionInList[] => {
  const newArray: OptionInList[] = []

  list.forEach((address) => {
    newArray.push({
      id: address.id,
      path: '/',
      title: `${address.street}, д. ${address.house_number ?? '-'}, под. ${address.entry ?? '-'}, эт. ${
        address.floor ?? '-'
      }, кв. ${address.apartment ?? '-'}`,
      subtitle: address.comment,
      handler: () => {
        localStorage.setItem('order', 'delivery')
      },
    })
  })

  return newArray
}

const goToMenu = () => {
  localStorage.setItem('order', 'delivery')
  localStorage.setItem('addressId', String(maxFillerLocationId.value))

  const locationObject = {
    id: maxFillerLocationId.value,
    title: 'Выбрать адрес доставки',
  }

  accountStore.setLocation(locationObject, 'delivery')
  localStorage.setItem('location', JSON.stringify(locationObject))

  emit('close', maxFilledLocationIiko.value)
}

onMounted(async () => {
  if (accountStore.orderType === 'delivery') {
    chosenOption.value = 1
  }

  // Создание списка пунктов самовывоза
  const { data: pickupData } = await getPickupPoints(Number(accountStore.cityId))
  const { data: locationData } = await getMaxFilledPickup(Number(accountStore.cityId))

  deliveryIsActive.value = pickupData.is_delivery_allowed
  maxFillerLocationId.value = locationData._id
  maxFilledLocationIiko.value = locationData.iiko_id

  if (!deliveryIsActive.value) {
    chosenOption.value = 0
    accountStore.orderType = 'pickup'
  } else {
    accountStore.orderType = 'delivery'
    chosenOption.value = 1
  }

  if (pickupData.pickup_points) mockPickup.value = generatePickupList(pickupData.pickup_points)

  // Создание списка адресов если пользователь авторизован
  if (accountStore.profile) {
    const { data: addressesData } = await getUserAddresses()
    if (addressesData) mockUserAddress.value = generateAddressesList(addressesData)
  }
})
</script>

<template>
  <div class="pickup">
    <input-switch
      v-if="deliveryIsActive"
      v-model="chosenOption"
      :options="['Самовывоз', 'Доставка']"
    />
    <h3 class="pickup__title">{{ pageTitle }}</h3>
    <ul v-if="chosenOption === 1" class="pickup__adresses">
      <div v-if="!accountStore.profile" class="pickup__adresses-auth">
        <img src="/assets/images/banners/mount.jpg" />
        <p>Добро пожаловать!</p>
        <span
          >Сначала выберите вкусные блюда из меню, а адрес доставки вы сможете указать позже — в
          корзине.</span
        >
      </div>
      <template v-else>
        <li v-for="(address, index) in mockUserAddress" :key="index">
          <input-radio v-model="selectedDeliveryAddress" :value="address.id">
            {{ address.title }}
          </input-radio>
        </li>
        <button-main :button-state="5" @click="emit('change-visible', 'new-address')">
          <template #start><icon-plus /></template>
          Добавить адрес
        </button-main>
      </template>
    </ul>
    <ul v-if="chosenOption === 0" class="pickup__adresses">
      <li v-for="(address, index) in mockPickup" :key="index">
        <input-radio v-model="selectedAddress" :value="address.title">
          {{ address.title }}
        </input-radio>
        <span>{{ address.subtitle }}</span>
      </li>
    </ul>
    <button-main v-if="chosenOption === 1 && !accountStore.profile" @click="goToMenu">
      Перейти в меню
    </button-main>
    <button-main v-else @click="setLocation" :disabled="isButtonDisabled"> Выбрать </button-main>
  </div>
</template>

<style scoped lang="scss">
.pickup {
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
  min-height: 67dvh;
  width: 457px;

  &__title {
    @include h2-1;
  }

  &__adresses {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 24px;
    height: 362px;
    padding: 0 8px 16px;
    overflow-y: scroll;

    &-auth {
      margin: auto 0;
      height: 100%;
      padding: 0 32px;
      text-align: center;
      color: $sub-text;

      p {
        margin-block: 12px;
        @include h2-2;
      }

      span {
        @include h5-6;
      }
    }

    li > span {
      margin-left: 24px;
      color: $sub-text;
      @include h5-2;
    }

    &::-webkit-scrollbar {
      width: 10px;
    }

    &::-webkit-scrollbar-track {
      background: $ui-gray-2;
      border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: $sub-text;
      border-radius: 10px;
      border: 2px solid $ui-gray-2;
    }
  }
}
</style>
